<script setup>
/* Imports */
import {
  inject,
  watch,
} from 'vue';
import VueCookies from 'vue-cookies';

/* Components */
import {
  sendAnalyticsEvent,
  sanitizeAnalyticsString,
} from '@garmin/analytics';
import MapNumberOfUpdates from './MapNumberOfUpdates.vue';
import MapPrice from './MapPrice.vue';
import ShopNowProductCardBtn from './ShopNowProductCardBtn.vue';

/* Helpers */
import {
  mapGetters,
  mapActions,
} from '../helpers/mainHelpers.js';

/* Inject */
const translations = inject('translations');
const isWorldwideUpdates = inject('isWorldwideUpdates');
const isHeatmap = inject('isHeatmap');

/* Store - Getters */
const {
  getPartNumber: partNumb,
  getDrawerMode: drawerMode,
  getCustomerGroups: customerGroups,
  getPricingByMapSku: pricingByMapSku,
  getHasDiscount: hasDiscount,
} = mapGetters();

/* Store - Actions */
const {
  removePolygon,
  setPolygon,
  setPartNumber,
  setDrawerMode,
  setIsWorldwideUpdates,
  fetchPrice,
} = mapActions();

/* Props */
const props = defineProps({
  maps: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

/* Methods */
const switchToggled = (partNumber) => partNumb.value === partNumber;

const showNumberOfUpdates = (partNumber) => switchToggled(partNumber) && !isWorldwideUpdates.value && isHeatmap.value;

/**
 * Returns the coordinates array for the polygon
 * @param {object} polygon - The polygon object
 * @returns {Array}        - The coordinates array
 */
const getCoordinates = (polygon) => {
  if (polygon.type === 'MultiPolygon') {
    return polygon.multiPolygonCoordinates;
  }
  if (polygon.type === 'Polygon') {
    return polygon.polygonCoordinates;
  }
  return [];
};

/* Handle the drawer mode */
const changeDrawerMode = () => {
  // when user clicks view on map and the drawer is full height,
  // make the drawer half way, so that the user can see the map
  if (drawerMode.value === 'top') {
    setDrawerMode('middle');
  }
};

/**
 * Checks coordinates array to see if any value crosses anti-meridian
 * Returns true if coordinates are good as is
 * Returns false if coordinates need adjusted
 * @param {Array} coordinates - The coordinates array
 * @returns {boolean}         - True if coordinates are good as is, false if coordinates need adjusted
 */
const checkCoordinates = (coordinates) => {
  let valid = false;
  if (coordinates) {
    let longsOverZero = 0;
    let totalLatLongs = 0;
    let maxLongitude = null;
    let minLongitude = null;

    const totalPolygons = coordinates.length;
    for (let i = 0; i < totalPolygons; i += 1) {
      const singlePolygon = coordinates[i];

      // Hardcode [0] since inner array is always at [0]
      const singlePolygonCoordinateCount = singlePolygon[0].length;
      for (let j = 0; j < singlePolygonCoordinateCount; j += 1) {
        const longitude = coordinates[i][0][j][1];

        if (!maxLongitude || maxLongitude < longitude) {
          maxLongitude = longitude;
        }
        if (!minLongitude || minLongitude > longitude) {
          minLongitude = longitude;
        }
        if (longitude >= 0) {
          longsOverZero += 1;
        }
        totalLatLongs += 1;
      }
    }
    const allSameSign = longsOverZero === 0 || longsOverZero === totalLatLongs;

    if (allSameSign) {
      valid = true;
    } else {
      valid = maxLongitude - minLongitude < 180;
    }
  }
  return valid;
};

/**
 * This fixes a known issue with Leaflet when a polygon crosses the anti-meridian.
 * We subtract 360 from any longitude value > 0 to avoid it wrapping around the map.
 * @param {Array} coordinates - The coordinates array
 * @returns {Array}           - The new coordinates array
 */
const processCoordinates = (coordinates) => {
  // Create a deep copy of the coordinates array
  const newCoordinates = JSON.parse(JSON.stringify(coordinates));

  const totalPolygons = newCoordinates.length;
  for (let i = 0; i < totalPolygons; i += 1) {
    const singlePolygon = newCoordinates[i];

    // Hardcode [0] since inner array is always at [0]
    const singlePolygonCoordinateCount = singlePolygon[0].length;
    for (let j = 0; j < singlePolygonCoordinateCount; j += 1) {
      const longitude = newCoordinates[i][0][j][1];
      if (longitude > 0) {
        newCoordinates[i][0][j][1] = longitude - 360;
      }
    }
  }

  return newCoordinates;
};

/* Handle the polygon */
const changePolygon = (map) => {
  changeDrawerMode();
  const { polygon } = map;
  const partNumber = map.sku;
  if (partNumber === partNumb.value) {
    removePolygon();
    // Show worldwide updates if toggle is off
    setIsWorldwideUpdates(true);
  } else {
    let coordinates = getCoordinates(polygon);

    // Checks if longitude values cross anti-meridian
    const validCoordinates = checkCoordinates(coordinates);

    // Subtract 360 from longitude values > 0 to fix anti-meridian wrapping
    if (!validCoordinates) {
      coordinates = processCoordinates(coordinates);
    }

    setPolygon({
      dashed: polygon.dashed,
      type: polygon.type,
      coordinates,
    });
    setPartNumber(partNumber);
    // Hide worldwide updates if toggle is on
    setIsWorldwideUpdates(false);
  }
};

const sendViewItemListAnalyticsEvent = () => {
  const pricing = (map) => ({
    listPrice: pricingByMapSku.value[map?.sku]?.unformatted?.listPrice,
    salePrice: pricingByMapSku.value[map?.sku]?.unformatted?.salePrice,
  });
  const analyticsData = {
    employee_flag: customerGroups?.value === 'US' ? 'true' : 'false',
    login_status: VueCookies.get('GARMIN-SSO-CUST-GUID') ? 'Logged In' : 'Not Logged In',
    item_list_id: props.maps?.map(() => 'marine_maps_sidebar'),
    item_list_name: props.maps?.map(() => 'Marine Maps Sidebar'),
    index: props.maps?.map((_, index) => index + 1),
    product_id: props.maps?.map((item) => item?.buyUrl?.split('/p/')[1]?.split('/')[0] || 'NA'),
    product_name: props.maps?.map((item) => sanitizeAnalyticsString(item?.name) || 'NA'),
    product_url: props.maps?.map((item) => item?.buyUrl || 'NA'),
    product_price: props.maps?.map((item) => pricing(item).salePrice || pricing(item).listPrice || 'NA'),
    is_sale: props.maps?.map((item) => (hasDiscount.value(item?.sku) ? 'yes' : 'no')),
    price_shown: props.maps?.map((item) => (pricing(item).listPrice ? 'yes' : 'no')),
    item_family: 'NA',
    tealium_event: 'view_item_list',
  };
  sendAnalyticsEvent(analyticsData);
};

const fetchPricing = async () => {
  const pricingRequests = props.maps?.map((map) => {
    // Only fetch pricing if we don't already have it in the store
    if (!pricingByMapSku.value[map.sku]) {
      return fetchPrice(map);
    }
    return Promise.resolve(pricingByMapSku.value[map.sku]);
  }) || [];
  await Promise.allSettled(pricingRequests);
};

watch(() => props.maps, async (newMaps) => {
  if (newMaps?.length) {
    await fetchPricing();
    sendViewItemListAnalyticsEvent();
  }
}, { immediate: true });
</script>

<template>
  <div
    v-if="!props.isLoading"
    class="result-block__wrap"
  >
    <div
      v-for="(map, index) in props.maps"
      :key="index"
      class="result-block"
      data-testid="result-block"
    >
      <div
        class="result-block__info"
        @click.prevent="changePolygon(map)"
        @keyup.enter.prevent="changePolygon(map)"
      >
        <g-heading
          class="result-block__info__title"
          size="3"
        >
          <h3 v-html="map?.name" />
        </g-heading>
        <MapPrice
          :map="map"
          :index="index"
        />
        <g-switch
          class="result-block__switch"
          size="large"
          :right-label="translations.ITFE_MARINE_MAPS_VIEW_ON_MAP"
          tabindex="0"
        >
          <input
            type="checkbox"
            name="resultSwitch"
            :checked="switchToggled(map.sku)"
            @update="changePolygon(map)"
          >
        </g-switch>
        <MapNumberOfUpdates
          v-if="showNumberOfUpdates(map.sku)"
          class="result-block__number-of-updates"
          :map-multi-polygon-coordinates="map.polygon?.multiPolygonCoordinates"
        />
      </div>
      <ShopNowProductCardBtn
        :map="map"
        :index="index"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.result-block__wrap {
  display: flex;
  padding: 0 1.25rem;
  gap: 1.25rem;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  margin: 0 -1.25rem;

  @include breakpoint('sm') {
    display: revert;
    padding: revert;
    gap: revert;
    overflow-x: revert;
    scroll-snap-type: revert;
    margin: revert;
  }
}

.result-block {
  @include make-drop-shadow();
  display: flex;
  flex-direction: column;
  padding: 1rem;
  background: $color-white;
  margin-bottom: 1rem;
  min-width: 80vw;
  scroll-snap-align: center;

  @include breakpoint('sm') {
    min-width: revert;
    scroll-snap-align: revert;

    &:first-child {
      margin-top: 1.25rem;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__info {
    width: 100%;

    &:hover {
      cursor: pointer;
    }

    &__title {
      color: $color-black;

      :deep(h3) {
        line-height: 1.25;
        margin-top: 0;
      }
    }
  }

  &__switch {
    margin-bottom: 1rem;
  }

  &__number-of-updates {
    border-top: 2px solid $color-gray-85;
    padding-top: 1rem;
  }

  // font-size for the switch label
  :deep(.g__switch__label__text--right) {
    font-size: 0.875rem;
    @include font-primary-weight-medium();
  }

  /**
  * g-switch -> Global Component
  * Prevent the input from breaking the screen
  * when the user navigates through cards by pressing tab
  */

  .g__switch input {
    display: inline-block;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    visibility: hidden;
  }
}
</style>
